import React, { createContext, useContext, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isPasswordProtected, setIsPasswordProtected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // 检查认证状态
  const { data: authStatus, refetch: refetchAuthStatus } = useQuery({
    queryKey: ['auth-status'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/auth/status', {
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error('Failed to check auth status')
        }
        return response.json()
      } catch (error) {
        console.error('Auth status check failed:', error)
        // 返回默认状态而不是抛出错误
        return { passwordProtected: false, authenticated: false }
      }
    },
    retry: 1, // 减少重试次数
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5分钟
    refetchOnMount: true,
  })

  useEffect(() => {
    if (authStatus) {
      setIsPasswordProtected(authStatus.passwordProtected || false)
      setIsAuthenticated(authStatus.authenticated || !authStatus.passwordProtected)
      setIsLoading(false)
    } else {
      // 如果没有获取到状态，设置默认值
      setIsLoading(false)
      setIsPasswordProtected(false)
      setIsAuthenticated(true) // 默认允许访问
    }
  }, [authStatus])

  const login = async () => {
    try {
      const result = await refetchAuthStatus()
      if (result.data) {
        setIsPasswordProtected(result.data.passwordProtected)
        setIsAuthenticated(result.data.authenticated || !result.data.passwordProtected)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Login refresh failed:', error)
    }
  }

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setIsAuthenticated(false)
      setIsLoading(false)
      // 不需要重新获取状态，因为我们已经知道用户已登出
    }
  }

  const value = {
    isAuthenticated,
    isPasswordProtected,
    isLoading,
    login,
    logout,
    refetchAuthStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
